import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subject, catchError, debounceTime, merge, switchMap, tap } from 'rxjs';
import { LoaderComponent } from '@components/app-loader/app-loader.component';
import { ModalDatePickerComponent } from '@components/Modals/modal-date-picker/modal-date-picker.component';
import { ModalZTicketComponent } from '@components/Modals/modal-z-ticket/modal-z-ticket.component';
import { PaginationComponent } from '@components/pagination/pagination.component';
import { SvgComponent } from '@components/svg.component';
import {
  SessionListFilters,
  SessionModel,
  SessionSort,
  SessionSortOrder,
  SessionStatusFilter,
} from '@models/session/session.model';
import { LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from '@helpers/list-config';
import { downloadBlob } from '@helpers/file-download.helper';
import { HttpError } from '@models/http-error.model';
import { PaginationInfo } from '@models/shared/pagination-info.model';
import { StatusFilterConfig } from '@models/shared/status-filter-config.model';
import { HistoryService } from '@services/history.service';
import { SessionService } from '@services/session.service';

@Component({
  selector: 'app-session-list-page',
  imports: [
    TranslateModule,
    DatePipe,
    ReactiveFormsModule,
    SvgComponent,
    LoaderComponent,
    PaginationComponent,
    ModalDatePickerComponent,
    ModalZTicketComponent,
  ],
  templateUrl: './session-list-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class SessionListPageComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly historyService = inject(HistoryService);
  private readonly sessionService = inject(SessionService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  private readonly pageRequests = new Subject<number>();

  protected readonly statusFilters: StatusFilterConfig<SessionStatusFilter>[] = [
    {
      value: 'all',
      labelKey: 'common.all',
      bg: 'bg-bleu-1',
      fg: 'fg-bleu-1',
      border: 'border-bleu-1',
    },
    {
      value: 'open',
      labelKey: 'session_list.filter.open',
      bg: 'bg-vert-1',
      fg: 'fg-vert-1',
      border: 'border-vert-1',
    },
    {
      value: 'closed',
      labelKey: 'session_list.filter.closed',
      bg: 'bg-gris-1',
      fg: 'fg-gris-1',
      border: 'border-gris-1',
    },
  ];

  protected readonly sessions = signal<SessionModel[]>([]);
  protected readonly pagination = signal<PaginationInfo | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly showDatePicker = signal(false);
  protected readonly zTicket = signal<{ sessionId: number; pdf: Blob } | null>(null);
  protected readonly zTicketLoadingSessionId = signal<number | null>(null);
  protected readonly isZTicketLoading = computed(() => this.zTicketLoadingSessionId() !== null);
  protected readonly statusFilter = signal<SessionStatusFilter>('all');
  protected readonly sort = signal<SessionSort>('id');
  protected readonly sortOrder = signal<SessionSortOrder>('desc');
  protected readonly sortIcon = computed(() =>
    this.sortOrder() === 'desc' ? 'arrow_downward' : 'arrow_upward'
  );
  protected readonly sessionNumberControl = new FormControl<number | null>(null);
  protected readonly dateControl = new FormControl('', { nonNullable: true });

  constructor() {
    this.pageRequests
      .pipe(
        tap(() => this.isLoading.set(true)),
        switchMap(page =>
          this.sessionService.getSessions(page, LIST_PAGE_SIZE, this.#currentFilters()).pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.toastr.error(
                this.translate.instant('session_list.errors.load_error_message'),
                this.translate.instant('session_list.errors.load_error_title')
              );
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(result => {
        this.sessions.set(result.data);
        this.pagination.set(result.pagination);
        this.isLoading.set(false);
      });

    merge(this.sessionNumberControl.valueChanges, this.dateControl.valueChanges)
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe(() => this.loadPage(1));

    this.loadPage(1);
  }

  goBack(): void {
    this.historyService.back();
  }

  loadPage(page: number): void {
    this.pageRequests.next(page);
  }

  setStatusFilter(filter: SessionStatusFilter): void {
    this.statusFilter.set(filter);
    this.loadPage(1);
  }

  setSort(sort: SessionSort): void {
    if (this.sort() === sort) {
      this.sortOrder.update(order => (order === 'desc' ? 'asc' : 'desc'));
    } else {
      this.sort.set(sort);
      this.sortOrder.set('desc');
    }
    this.loadPage(1);
  }

  protected openDatePicker(): void {
    this.showDatePicker.set(true);
  }

  protected closeDatePicker(): void {
    this.showDatePicker.set(false);
  }

  protected onDateSelected(date: string): void {
    this.dateControl.setValue(date);
    this.closeDatePicker();
  }

  protected onDateCleared(): void {
    this.clearDate();
    this.closeDatePicker();
  }

  protected clearDate(): void {
    this.dateControl.setValue('');
  }

  protected openZTicket(session: SessionModel): void {
    this.zTicketLoadingSessionId.set(session.id);

    this.sessionService
      .getZTicket(session.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: pdf => {
          this.zTicketLoadingSessionId.set(null);
          this.zTicket.set({ sessionId: session.id, pdf });
        },
        error: (error: HttpError) => {
          this.zTicketLoadingSessionId.set(null);
          this.#showZTicketError(error);
        },
      });
  }

  protected downloadZTicket(): void {
    const ticket = this.zTicket();
    if (!ticket) return;

    downloadBlob(ticket.pdf, `ticket-z-session-${ticket.sessionId}.pdf`);
  }

  protected closeZTicket(): void {
    this.zTicket.set(null);
  }

  #showZTicketError(error: HttpError): void {
    const translatedMessage = this.translate.instant(error.message);
    const hasTranslation = translatedMessage !== error.message;

    this.toastr.error(
      hasTranslation
        ? translatedMessage
        : this.translate.instant('session_list.z_ticket.errors.load_error_message'),
      this.translate.instant('session_list.z_ticket.errors.load_error_title')
    );
  }

  #currentFilters(): SessionListFilters {
    const sessionNumber = this.sessionNumberControl.value;
    const isValidSessionNumber =
      sessionNumber !== null &&
      Number.isInteger(sessionNumber) &&
      sessionNumber >= 1;

    return {
      status: this.statusFilter(),
      sessionNumber: isValidSessionNumber ? sessionNumber : null,
      date: this.dateControl.value || null,
      sort: this.sort(),
      order: this.sortOrder(),
    };
  }
}
